/**
 * Browser/platform detection helpers for UserSettingsModal.
 *
 * Extracted from UserSettingsModal.tsx so the UA parsing and the
 * browser-specific notification instructions can be unit tested without
 * a DOM (see Testing Pattern in CLAUDE.md).
 */

export interface BrowserInfo {
  name: string;
  isEdge: boolean;
  isChrome: boolean;
  isFirefox: boolean;
  isSafari: boolean;
  isIOS: boolean;
  isStandalone: boolean;
}

export interface BrowserInstructions {
  promptLocation: string;
  howToEnable: string;
  ifBlocked: string;
  ifTimeout: string;
}

const UNKNOWN_BROWSER: BrowserInfo = {
  name: 'unknown',
  isEdge: false,
  isChrome: false,
  isFirefox: false,
  isSafari: false,
  isIOS: false,
  isStandalone: false,
};

/**
 * Parse a user-agent string (plus platform hints) into browser/platform flags.
 * Pure function — pass navigator-derived values in for testability.
 */
export function parseBrowserInfo(
  ua: string,
  opts: {
    platform?: string;
    maxTouchPoints?: number;
    navigatorStandalone?: boolean;
    displayModeStandalone?: boolean;
  } = {}
): BrowserInfo {
  // Check if iOS (iPhone, iPad, iPod)
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (opts.platform === 'MacIntel' && (opts.maxTouchPoints ?? 0) > 1);

  // Check if running as standalone PWA (added to home screen)
  const isStandalone = Boolean(opts.navigatorStandalone) || Boolean(opts.displayModeStandalone);

  // Order matters: Edge includes "Chrome" in UA, so check Edge first
  if (/Edg\//.test(ua)) return { name: 'Edge', isEdge: true, isChrome: false, isFirefox: false, isSafari: false, isIOS, isStandalone };
  if (/Firefox\//.test(ua)) return { name: 'Firefox', isEdge: false, isChrome: false, isFirefox: true, isSafari: false, isIOS, isStandalone };
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return { name: 'Safari', isEdge: false, isChrome: false, isFirefox: false, isSafari: true, isIOS, isStandalone };
  if (/Chrome\//.test(ua)) return { name: 'Chrome', isEdge: false, isChrome: true, isFirefox: false, isSafari: false, isIOS, isStandalone };
  return { name: 'unknown', isEdge: false, isChrome: false, isFirefox: false, isSafari: false, isIOS, isStandalone };
}

/**
 * Detect browser and platform for specific notification permission messaging.
 * Reads from the live navigator/window — use parseBrowserInfo in tests.
 */
export function detectBrowserInfo(): BrowserInfo {
  if (typeof navigator === 'undefined') {
    return UNKNOWN_BROWSER;
  }
  return parseBrowserInfo(navigator.userAgent, {
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
    navigatorStandalone: 'standalone' in navigator && Boolean((navigator as unknown as { standalone?: boolean }).standalone),
    displayModeStandalone: window.matchMedia('(display-mode: standalone)').matches,
  });
}

/**
 * Get browser-specific instructions for enabling notifications
 */
export function getBrowserInstructions(browserInfo: BrowserInfo): BrowserInstructions {
  if (browserInfo.isEdge) {
    return {
      promptLocation: 'Look for a small bell icon (🔔) in the address bar on the right side.',
      howToEnable: 'Click the bell icon and select "Allow" to enable notifications.',
      ifBlocked: 'Go to edge://settings/content/notifications or Windows Settings → System → Notifications → Microsoft Edge.',
      ifTimeout: 'Check Windows Settings → System → Notifications and ensure notifications are enabled for Microsoft Edge.',
    };
  }
  if (browserInfo.isChrome) {
    return {
      promptLocation: 'A popup will appear below the address bar asking for permission.',
      howToEnable: 'Click "Allow" in the popup to enable notifications.',
      ifBlocked: 'Click the lock icon (🔒) in the address bar → Site settings → Notifications → Allow.',
      ifTimeout: 'Check your system notification settings and try again.',
    };
  }
  if (browserInfo.isFirefox) {
    return {
      promptLocation: 'A popup will appear at the top of the page asking for permission.',
      howToEnable: 'Click "Allow Notifications" in the popup.',
      ifBlocked: 'Click the lock icon (🔒) in the address bar → Clear permission for notifications, then try again.',
      ifTimeout: 'Check your system notification settings and try again.',
    };
  }
  if (browserInfo.isSafari) {
    return {
      promptLocation: 'A dialog will appear asking for permission.',
      howToEnable: 'Click "Allow" to enable notifications.',
      ifBlocked: 'Go to Safari → Settings → Websites → Notifications and allow this site.',
      ifTimeout: 'Check System Settings → Notifications → Safari and ensure notifications are enabled.',
    };
  }
  return {
    promptLocation: 'A prompt will appear asking for notification permission.',
    howToEnable: 'Click "Allow" to enable notifications.',
    ifBlocked: 'Check your browser settings to enable notifications for this site.',
    ifTimeout: 'Check your browser and system notification settings, then try again.',
  };
}

/**
 * Get icon class for device based on userAgent
 */
export function getDeviceIcon(userAgent: string): string {
  if (!userAgent) return 'fa-desktop';
  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'fa-apple';
  if (userAgent.includes('Android')) return 'fa-android';
  if (userAgent.includes('Windows')) return 'fa-windows';
  if (userAgent.includes('Mac OS')) return 'fa-apple';
  if (userAgent.includes('Linux')) return 'fa-linux';
  return 'fa-desktop';
}

/**
 * Format registration date for display
 */
export function formatRegistrationDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'Unknown date';
  }
}
